/**
 * Tanıtım takibi state (v0.1.169): GET /leads/outreach/log every time the screen opens (the last view stays on screen
 * while it loads), again on Yenile and on Tekrar dene after an error. Read only.
 * v0.1.170: a mail's text (GET /leads/outreach/log/{messageId}) is read when its row is opened and kept for the visit.
 */

import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import type { OutreachLog, OutreachLogText } from '../api/outreach-log.js';
import { useStore } from './context.js';

export interface OutreachTextState {
  text: OutreachLogText | null;
  error: string | null;
  loading: boolean;
}

export interface OutreachLogState {
  view: OutreachLog | null;
  error: string | null;
  loading: boolean;
  /** Mail texts read so far, by message id. */
  texts: Readonly<Record<string, OutreachTextState>>;
}

export class OutreachLogStore {
  private state: OutreachLogState = { view: null, error: null, loading: false, texts: {} };
  private inFlight: Promise<void> | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): OutreachLogState => this.state;

  private set(patch: Partial<OutreachLogState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  private setText(messageId: string, value: OutreachTextState): void {
    this.set({ texts: { ...this.state.texts, [messageId]: value } });
  }

  /** Reads again, keeping the current view meanwhile; one read at a time. The screen calls it on mount. */
  reload(): Promise<void> {
    if (this.inFlight !== null) return this.inFlight;
    // The drafts are rewritten hourly: a text read before is read again when its row is opened next.
    this.set({ loading: true, texts: {} });
    const run = (async () => {
      try {
        this.set({ view: await this.api.getOutreachLog(), error: null, loading: false });
      } catch (error) {
        this.set({ error: error instanceof Error ? error.message : String(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }

  /** Reads one mail's text unless it is already here or on its way; a failed read is tried again. */
  async loadText(messageId: string): Promise<void> {
    const current = this.state.texts[messageId];
    if (current !== undefined && (current.loading || current.text !== null)) return;
    this.setText(messageId, { text: null, error: null, loading: true });
    try {
      this.setText(messageId, { text: await this.api.getOutreachLogText(messageId), error: null, loading: false });
    } catch (error) {
      this.setText(messageId, {
        text: null,
        error: error instanceof Error ? error.message : String(error),
        loading: false,
      });
    }
  }
}

export function useOutreachLog(): { store: OutreachLogStore; state: OutreachLogState } {
  const store = useStore().outreachLog;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
